import { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";

import "../co/proposal.css";
import { parseRoomPath, roomPath } from "../room-path";
import { ds, dsCardSx } from "../theme/ds";

import type { PageTemplateProps } from "./types";

// The proposal report, brought into the room as it is (Eric, 2026-09-23):
// the supplier-listing-proposal HTML with its own stylesheet (co/proposal.css,
// scoped under .co-proposal), minus the brandmark, the packages and the
// review-call band; the room's own "Go to Your assortment" closes it. The
// room stores the report body per room (`data.html`, hero through the example
// product, sections carrying the anchor ids) and its footnotes
// (`data.sourcesHtml`); the sidebar lists `data.sections` as hash links.
// The HTML is written by the provisioning skill behind X-Provisioning-Token,
// never by a viewer, which is why it can be rendered as markup.
export interface ProposalData {
    html?: string;
    sourcesHtml?: string;
    sections?: { id: string; label: string }[];
}

export function COProposal({ page, room }: PageTemplateProps) {
    const d = (page.data ?? {}) as ProposalData;
    const { pathname, hash } = useLocation();
    // The proposal is the room's first page, served at the bare room URL, so
    // build from the room path rather than stripping a segment off pathname.
    const assortmentHref = `${roomPath(room.slug, parseRoomPath(pathname)?.preview ?? false, room.roomType)}/your-assortment`;

    // React Router changes the hash without scrolling; land on the anchor.
    useEffect(() => {
        if (!hash) return;
        const el = document.getElementById(hash.slice(1));
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [hash, d.html]);

    return (
        <Box className="co-proposal" sx={{ scrollMarginTop: 16, "& section": { scrollMarginTop: 16 } }}>
            <div dangerouslySetInnerHTML={{ __html: d.html ?? "" }} />
            <Box className="wrap" sx={{ py: 3 }}>
                <Box sx={{ ...dsCardSx, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 3 }}>
                    <Box sx={{ flex: "1 1 320px" }}>
                        <Typography sx={{ fontSize: ds.text.lg, fontWeight: 700, color: ds.navy, mb: 0.5 }}>
                            Pick the listings and services you want us to start with
                        </Typography>
                        <Typography sx={{ fontSize: ds.text.base, color: ds.gray600 }}>
                            Every ASIN is scored on Your assortment. Tick the services per ASIN, or apply the slider to select
                            every weak score at once, then check out.
                        </Typography>
                    </Box>
                    <Button component={RouterLink} to={assortmentHref} variant="contained" size="large">
                        Go to Your assortment
                    </Button>
                </Box>
            </Box>
            <div dangerouslySetInnerHTML={{ __html: d.sourcesHtml ?? "" }} />
        </Box>
    );
}
